import type { IContent } from '~/features-shared/content/interfaces/content'
import { useRefineGlobalContentsStore } from '~/features-shared/content-feed/composables/refine-contents'
import type { ServiceFetchControllerDrafts } from '~/features-shared/content-feed/controllers/service-fetch-controller-drafts'

/**
 * Composable that handles storage of drafts
 *
 * @param service The swym service that will be used to fetch the contents
 * @param noGlobalStorage Optional parameter if we do not want to get the contents from the global contents map
 * @returns properties that make up the store
 */
export function useRefineGlobalDraftsContentsStore(
  service: ServiceFetchControllerDrafts,
  noGlobalStorage?: boolean,
) {
  const globalContents = useRefineGlobalContentsStore(service, noGlobalStorage)

  /**
   * A filter function that filters only the draft contents
   *
   * @param content The content
   * @returns true if it is a draft
   */
  function isDraft(content?: IContent) {
    if (!content)
      return false

    return !content.published
  }

  /**
   * Getter method to get all draft contents
   *
   * @param context The context for the drafts
   * @returns All the contents
   */
  function getContents(context: string): IContent[] {
    return globalContents
      .getContents(context)
      .filter(isDraft)
  }

  /**
   * A computed for the contents of the current context
   */
  const currentContents = computed<IContent[]>(() => globalContents.context.value
    ? (getContents(globalContents.context.value) || [])
    : [])

  return {
    ...globalContents,

    // Overridden values
    currentContents,
    getContents,
  }
}
